import { useState, useCallback, useRef, useEffect } from 'react';
import { ImageIcon, Download, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const PlaceholderImage = () => {
  useToolUsage('placeholder-image');
  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(300);
  const [text, setText] = useState('');
  const [bgColor, setBgColor] = useState('#cccccc');
  const [textColor, setTextColor] = useState('#666666');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = textColor;
    const displayText = text || `${width}×${height}`;
    const fontSize = Math.min(width, height) / 8;
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(displayText, width / 2, height / 2);
  }, [width, height, text, bgColor, textColor]);

  useEffect(() => { draw(); }, [draw]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `placeholder_${width}x${height}.png`;
    a.click();
    toast.success('Downloaded!');
  };

  const copyDataUrl = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    navigator.clipboard.writeText(canvas.toDataURL('image/png'));
    toast.success('Data URL copied!');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <ImageIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Placeholder Image</h1>
          <p className="text-muted-foreground">Generate placeholder images for your designs</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Width</Label>
                <Input type="number" value={width} onChange={e => setWidth(Number(e.target.value))} min={1} max={4000} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Height</Label>
                <Input type="number" value={height} onChange={e => setHeight(Number(e.target.value))} min={1} max={4000} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Text (optional)</Label>
              <Input value={text} onChange={e => setText(e.target.value)} placeholder={`${width}×${height}`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Background</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                  <Input value={bgColor} onChange={e => setBgColor(e.target.value)} className="flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Text Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" />
                  <Input value={textColor} onChange={e => setTextColor(e.target.value)} className="flex-1" />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={download} className="flex-1"><Download className="h-4 w-4 mr-2" /> Download</Button>
              <Button variant="outline" onClick={copyDataUrl}><Copy className="h-4 w-4 mr-2" /> Copy URL</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 flex items-center justify-center">
            <canvas ref={canvasRef} className="max-w-full max-h-[400px] rounded-lg border border-border" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlaceholderImage;
